<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useEmitter } from 'dashboard/composables/emitter';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isAConversationRoute,
  isAInboxViewRoute,
} from 'dashboard/helper/routeHelpers';
import { useEventListener } from '@vueuse/core';

import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const route = useRoute();

const RECONNECTED_BANNER_TIMEOUT = 2000;

const showNotification = ref(!navigator.onLine);
const isDisconnected = ref(false);
const isReconnecting = ref(false);
const isReconnected = ref(false);
let reconnectTimeout = null;

const notificationState = computed(() => {
  if (isReconnecting.value) return 'reconnecting';
  if (isReconnected.value) return 'reconnected';
  return 'offline';
});

const notificationContent = computed(() => {
  if (notificationState.value === 'reconnecting') {
    return {
      title: t('NETWORK.NOTIFICATION.RECONNECTING_TITLE'),
      description: t('NETWORK.NOTIFICATION.RECONNECTING_DESCRIPTION'),
    };
  }

  if (notificationState.value === 'reconnected') {
    return {
      title: t('NETWORK.NOTIFICATION.RECONNECTED_TITLE'),
      description: t('NETWORK.NOTIFICATION.RECONNECTED_DESCRIPTION'),
    };
  }

  return {
    title: t('NETWORK.NOTIFICATION.OFFLINE_TITLE'),
    description: t('NETWORK.NOTIFICATION.OFFLINE_DESCRIPTION'),
  };
});

const iconName = computed(() => {
  if (isReconnected.value) return 'wifi';
  return 'wifi-off';
});
const iconClasses = computed(() => ({
  'bg-n-amber-3 text-n-amber-11': notificationState.value === 'offline',
  'bg-n-blue-3 text-n-blue-11': notificationState.value === 'reconnecting',
  'bg-n-teal-3 text-n-teal-11': notificationState.value === 'reconnected',
}));
const canRefresh = computed(
  () => !isReconnecting.value && !isReconnected.value
);

const refreshPage = () => {
  window.location.reload();
};

const closeNotification = () => {
  showNotification.value = false;
  isReconnected.value = false;
  clearTimeout(reconnectTimeout);
};

const isInAnyOfTheRoutes = routeName => {
  return (
    isAConversationRoute(routeName, true) || isAInboxViewRoute(routeName, true)
  );
};

const updateWebsocketStatus = () => {
  isDisconnected.value = true;
  showNotification.value = true;
};

const handleReconnectionCompleted = () => {
  isDisconnected.value = false;
  isReconnecting.value = false;
  isReconnected.value = true;
  showNotification.value = true;
  reconnectTimeout = setTimeout(closeNotification, RECONNECTED_BANNER_TIMEOUT);
};

const handleReconnecting = () => {
  if (isInAnyOfTheRoutes(route.name)) {
    isReconnecting.value = true;
    isReconnected.value = false;
    showNotification.value = true;
  } else {
    handleReconnectionCompleted();
  }
};

const updateOnlineStatus = event => {
  // Case: Websocket is not disconnected
  // If the app goes offline, show the notification
  // If the app goes online, close the notification

  // Case: Websocket is disconnected
  // If the app goes offline, show the notification
  // If the app goes online but the websocket is disconnected, don't close the notification
  // If the app goes online and the websocket is not disconnected, close the notification

  if (event.type === 'offline') {
    showNotification.value = true;
  } else if (event.type === 'online' && !isDisconnected.value) {
    handleReconnectionCompleted();
  }
};

useEventListener('online', updateOnlineStatus);
useEventListener('offline', updateOnlineStatus);
useEmitter(BUS_EVENTS.WEBSOCKET_DISCONNECT, updateWebsocketStatus);
useEmitter(
  BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED,
  handleReconnectionCompleted
);
useEmitter(BUS_EVENTS.WEBSOCKET_RECONNECT, handleReconnecting);

onBeforeUnmount(() => {
  clearTimeout(reconnectTimeout);
});
</script>

<template>
  <div
    class="pointer-events-none fixed left-1/2 top-4 z-[100] w-[calc(100%-2rem)] max-w-md -translate-x-1/2"
  >
    <transition name="network-notification">
      <section
        v-show="showNotification"
        role="status"
        aria-live="polite"
        class="pointer-events-auto flex items-start gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3 shadow-xl"
      >
        <span
          class="mt-0.5 grid size-10 shrink-0 place-items-center rounded-xl"
          :class="iconClasses"
        >
          <fluent-icon :icon="iconName" size="20" />
        </span>

        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-n-slate-12">
            {{ notificationContent.title }}
          </p>
          <p class="mt-0.5 text-xs leading-5 text-n-slate-11">
            {{ notificationContent.description }}
          </p>
          <Button
            v-if="canRefresh"
            faded
            amber
            sm
            icon="i-lucide-refresh-ccw"
            :label="$t('NETWORK.BUTTON.RETRY')"
            class="mt-2"
            @click="refreshPage"
          />
        </div>

        <Button
          ghost
          slate
          sm
          icon="i-lucide-x"
          :title="$t('NETWORK.BUTTON.DISMISS')"
          :aria-label="$t('NETWORK.BUTTON.DISMISS')"
          class="-mr-1 -mt-1 shrink-0"
          @click="closeNotification"
        />
      </section>
    </transition>
  </div>
</template>

<style scoped>
.network-notification-enter-active,
.network-notification-leave-active {
  transition:
    opacity 180ms ease,
    transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
}

.network-notification-enter-from,
.network-notification-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.98);
}

@media (prefers-reduced-motion: reduce) {
  .network-notification-enter-active,
  .network-notification-leave-active {
    transition: none;
  }
}
</style>
